/**
 * ProductsPagination — bottom-bar controls for the Products table.
 *
 * Renders:
 *   • "N–M / T ürün" range on the left
 *   • Page navigation (< 1 2 3 … N >) with ellipsis in the middle
 *   • Per-page selector (25 / 50 / 100 / 200)
 *
 * Keeps the merchant's mental model: 1-indexed page numbers, disabled at
 * bounds, and a compact windowed list so 400+ pages don't overflow.
 */
import { ChevronLeft, ChevronRight } from "lucide-react";

const windowed = (page, totalPages) => {
    // Show first, last, current ± 1 and ellipses where needed.
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, page, page - 1, page + 1]);
    // Neighbours of first + last for smoother visual anchoring.
    if (page <= 4) pages.add(2).add(3).add(4);
    if (page >= totalPages - 3) pages.add(totalPages - 1).add(totalPages - 2).add(totalPages - 3);
    const sorted = [...pages].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
        out.push(sorted[i]);
        if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push("…");
    }
    return out;
};

const PageBtn = ({ children, active, disabled, onClick, testId, ariaLabel }) => (
    <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        aria-current={active ? "page" : undefined}
        aria-label={ariaLabel}
        data-testid={testId}
        className={`h-7 min-w-[28px] px-2 rounded-md text-[11px] font-medium tabular-nums transition-colors
            ${active
                ? "bg-foreground text-background border border-foreground"
                : "border border-border bg-surface hover:bg-muted/50 text-foreground/90"}
            ${disabled ? "opacity-40 cursor-not-allowed hover:bg-surface" : ""}
        `}
    >
        {children}
    </button>
);

export const ProductsPagination = ({
    page,
    limit,
    total,
    visibleCount,
    onPageChange,
    onLimitChange,
    testId = "products-pagination",
    limits = [25, 50, 100, 200],
}) => {
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const from = total === 0 ? 0 : (safePage - 1) * limit + 1;
    const to = Math.min(safePage * limit, total);

    return (
        <div className="mt-3 flex items-center justify-between gap-3 px-1 text-xs text-muted-foreground" data-testid={testId}>
            <span className="tabular-nums" data-testid="products-range">
                {total === 0 ? (
                    <span>0 ürün</span>
                ) : (
                    <>
                        <span className="font-mono num text-foreground/90">{from}–{to}</span>
                        <span> / {total} ürün</span>
                    </>
                )}
            </span>
            <div className="flex items-center gap-3">
                {/* Per-page selector */}
                <div className="flex items-center gap-1.5">
                    <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Sayfa başı</span>
                    <select
                        value={limit}
                        onChange={(e) => onLimitChange?.(Number(e.target.value))}
                        className="h-7 rounded-md border border-border bg-surface px-1.5 text-[11px] font-mono num text-foreground focus:outline-none focus:ring-1 focus:ring-foreground/30"
                        data-testid="products-per-page"
                        aria-label="Sayfa başı ürün sayısı"
                    >
                        {limits.map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </div>

                {/* Page numbers */}
                <nav className="flex items-center gap-0.5" aria-label="Ürün sayfaları">
                    <PageBtn
                        onClick={() => onPageChange?.(safePage - 1)}
                        disabled={safePage <= 1}
                        testId="products-prev-page"
                        ariaLabel="Önceki sayfa"
                    >
                        <ChevronLeft className="h-3 w-3" />
                    </PageBtn>
                    {windowed(safePage, totalPages).map((n, i) =>
                        n === "…" ? (
                            <span key={`gap-${i}`} className="px-1 text-muted-foreground/70">…</span>
                        ) : (
                            <PageBtn
                                key={n}
                                onClick={() => onPageChange?.(n)}
                                active={n === safePage}
                                testId={`products-page-${n}`}
                                ariaLabel={`Sayfa ${n}`}
                            >
                                {n}
                            </PageBtn>
                        )
                    )}
                    <PageBtn
                        onClick={() => onPageChange?.(safePage + 1)}
                        disabled={safePage >= totalPages}
                        testId="products-next-page"
                        ariaLabel="Sonraki sayfa"
                    >
                        <ChevronRight className="h-3 w-3" />
                    </PageBtn>
                </nav>
            </div>
        </div>
    );
};

export default ProductsPagination;
